import React from 'react';
import { Box, Flex, Text, Link, Spinner } from 'pounceV2';
import { useTheme } from '@mui/material';
import { Link as RRLink } from 'react-router-dom';
import urls from 'Source/urls';
import { AISummarizeKind } from 'Generated/schema';
import { conversationInput } from 'Pages/AI/graphql/aiConversations';
import { useGetRiskScorePopoverData } from './graphql/getRiskScorePopoverData.generated';

interface RiskScoreEvidencePopoverV3Props {
  alertId: string;
  isOpen: boolean;
}

const RiskScoreEvidencePopoverV3: React.FC<RiskScoreEvidencePopoverV3Props> = ({
  alertId,
  isOpen,
}) => {
  const {
    palette: { tokens },
  } = useTheme();

  const { data, previousData, loading } = useGetRiskScorePopoverData({
    variables: conversationInput(alertId, AISummarizeKind.Alert, 1),
    skip: !isOpen,
  });

  const conversationNode = (data ?? previousData)?.aiConversations?.edges?.[0]?.node;
  const conversationId = conversationNode?.id;
  const riskScore = conversationNode?.streams?.edges?.[0]?.node?.riskScoreEvidence;
  const detailsUrl = conversationId
    ? `${urls.alerts.details(alertId)}?conversationId=${conversationId}`
    : urls.alerts.details(alertId);

  return (
    <Box
      maxWidth={400}
      backgroundColor={tokens.background.neutral.midToWhite.base}
      borderRadius="medium"
      p={4}
    >
      {loading && (
        <Flex direction="column" alignItems="center">
          <Box mb={3}>
            <Spinner size="small" />
          </Box>
          <Text variant="body/sm">Loading risk score details...</Text>
        </Flex>
      )}

      {!loading && !riskScore && <Text variant="body/sm">Risk score details unavailable</Text>}

      {!loading && riskScore && (
        <>
          {/* Quick Summary */}
          <Flex direction="column" mb={3}>
            <Text fontWeight="bold" color={tokens.content.text.neutral[3]} mb={1}>
              Indicators
            </Text>
            <Flex gap={2}>
              <Text>{riskScore.benignIndicators?.length ?? 0} Benign</Text>
              <Text fontWeight="bold" color={tokens.content.text.semantic.error.base}>
                {riskScore.riskyIndicators?.length ?? 0} Risky
              </Text>
            </Flex>
          </Flex>

          {/* Risk Score */}
          {riskScore.normalizedTotalRiskScore !== undefined && (
            <Flex direction="column" mb={3}>
              <Text fontWeight="bold" color={tokens.content.text.neutral[3]} mb={1}>
                Risk Score
              </Text>
              <Text variant="body/sm" fontWeight="medium">
                {riskScore.normalizedTotalRiskScore.toFixed(2)}
              </Text>
            </Flex>
          )}

          {/* Link to AI Conversation */}
          <Box>
            <Link as={RRLink} to={detailsUrl} aria-label="View detailed evidence" size="small">
              View detailed evidence →
            </Link>
          </Box>
        </>
      )}
    </Box>
  );
};

export default RiskScoreEvidencePopoverV3;
